import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import {
  Wallet, Plus, Camera, Receipt, Loader2, CheckCircle2, RotateCcw, Trash2, X,
} from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const CATEGORIES = [
  { id: "yemek",   label: "Yemek",           color: "bg-orange-500/15 border-orange-500/40 text-orange-300" },
  { id: "sanayi",  label: "Sanayi / Parça",  color: "bg-blue-500/15 border-blue-500/40 text-blue-300" },
  { id: "garaj",   label: "Garaj / Yakıt",   color: "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" },
  { id: "diger",   label: "Diğer",           color: "bg-slate-500/15 border-slate-400/40 text-slate-300" },
];
const CAT_META = Object.fromEntries(CATEGORIES.map((c) => [c.id, c]));
const fmtTl = (n) => `₺ ${Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return iso || ""; } };

export default function Kasa() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("pending"); // pending | paid | new
  const [summary, setSummary] = useState({ pending_count: 0, pending_total: 0, paid_count: 0, paid_total: 0, scope: "own" });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyIds, setBusyIds] = useState(() => new Set());
  const [previewUrl, setPreviewUrl] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: l }] = await Promise.all([
        api.get("/expenses/summary"),
        api.get("/expenses", { params: { status: tab === "new" ? "pending" : tab } }),
      ]);
      setSummary(s);
      setItems(l.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Yüklenemedi");
    } finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { refresh(); }, [refresh]);

  const setBusy = (id, on) => setBusyIds((prev) => {
    const n = new Set(prev); on ? n.add(id) : n.delete(id); return n;
  });
  const markPaid = async (id) => {
    if (!isAdmin) return; setBusy(id, true);
    try { await api.post(`/expenses/${id}/mark-paid`); toast.success("Ödendi"); refresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "İşlem başarısız"); }
    finally { setBusy(id, false); }
  };
  const reopen = async (id) => {
    if (!isAdmin) return; setBusy(id, true);
    try { await api.post(`/expenses/${id}/reopen`); toast.success("Bekleyene geri alındı"); refresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "İşlem başarısız"); }
    finally { setBusy(id, false); }
  };
  const remove = async (id) => {
    if (!isAdmin) return;
    if (!window.confirm("Bu masraf kaydı silinsin mi?")) return;
    setBusy(id, true);
    try { await api.delete(`/expenses/${id}`); toast.success("Silindi"); refresh(); }
    catch (e) { toast.error("Silinemedi"); }
    finally { setBusy(id, false); }
  };
  const openReceipt = async (it) => {
    if (!it.receipt_path) return;
    try {
      const res = await api.get(`/expenses/${it.id}/receipt`, { responseType: "blob" });
      setPreviewUrl(URL.createObjectURL(res.data));
    } catch { toast.error("Fiş görüntülenemedi"); }
  };
  const closePreview = () => { if (previewUrl) URL.revokeObjectURL(previewUrl); setPreviewUrl(null); };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="kasa-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-md bg-emerald-500/20 flex items-center justify-center">
          <Wallet className="w-5 h-5 text-emerald-400" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--y-muted)]">Kasa</div>
          <div className="font-heading font-bold text-2xl text-[color:var(--y-text)]">Cepten Masraflar</div>
          <div className="text-xs text-[color:var(--y-muted)] mt-0.5">
            {isAdmin ? "Tüm kullanıcıların masrafları" : "Sadece kendi girdiğiniz masraflar görüntülenir"}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Card className="p-4 border-red-500/30 bg-red-500/5">
          <div className="text-[10px] uppercase tracking-widest text-red-300">Hazırlanacak Nakit</div>
          <div className="mt-1 font-mono font-bold text-2xl text-red-200">{fmtTl(summary.pending_total)}</div>
          <div className="text-[11px] text-red-300/70 mt-0.5">{summary.pending_count} bekleyen kayıt</div>
        </Card>
        <Card className="p-4 border-emerald-500/30 bg-emerald-500/5">
          <div className="text-[10px] uppercase tracking-widest text-emerald-300">Ödenen Toplam</div>
          <div className="mt-1 font-mono font-bold text-2xl text-emerald-200">{fmtTl(summary.paid_total)}</div>
          <div className="text-[11px] text-emerald-300/70 mt-0.5">{summary.paid_count} kayıt</div>
        </Card>
      </div>

      <div className="flex gap-2 border-b border-[color:var(--y-border)]">
        <TabBtn active={tab === "pending"} onClick={() => setTab("pending")} tid="kasa-tab-pending">Bekleyen ({summary.pending_count})</TabBtn>
        <TabBtn active={tab === "paid"} onClick={() => setTab("paid")} tid="kasa-tab-paid">Ödenen</TabBtn>
        <TabBtn active={tab === "new"} onClick={() => setTab("new")} tid="kasa-tab-new" highlight><Plus className="w-3.5 h-3.5 inline mr-1" />Yeni Masraf</TabBtn>
      </div>

      {tab === "new" ? (
        <NewExpenseForm onDone={() => { setTab("pending"); refresh(); }} />
      ) : (
        <div className="space-y-2">
          {loading && <div className="text-center py-8 text-[color:var(--y-muted)]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Yükleniyor...</div>}
          {!loading && items.length === 0 && (
            <div className="text-center py-14 text-[color:var(--y-muted)] text-sm">
              {tab === "pending" ? "Bekleyen masraf yok." : "Kayıt yok."}
            </div>
          )}
          {items.map((it) => (
            <Card key={it.id} className="p-3 flex items-center gap-3" data-testid={`kasa-item-${it.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[color:var(--y-text)] truncate">{it.description}</div>
                <div className="text-[11px] text-[color:var(--y-muted)] mt-0.5">
                  <span className={`inline-block px-1.5 py-0.5 border rounded ${CAT_META[it.category]?.color}`}>{CAT_META[it.category]?.label || it.category}</span>
                  <span className="ml-2">{it.spender_name}</span>
                  <span className="ml-2">{fmtDate(it.created_at)}</span>
                </div>
              </div>
              <div className="font-mono font-bold text-lg text-[color:var(--y-text)]">{fmtTl(it.amount_tl)}</div>
              {it.receipt_path && (
                <Button variant="outline" size="sm" onClick={() => openReceipt(it)} data-testid={`kasa-receipt-${it.id}`}>
                  <Receipt className="w-3.5 h-3.5" />
                </Button>
              )}
              {isAdmin && it.status === "pending" && (
                <Button size="sm" onClick={() => markPaid(it.id)} disabled={busyIds.has(it.id)} data-testid={`kasa-mark-paid-${it.id}`}>
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Ödendi
                </Button>
              )}
              {isAdmin && it.status === "paid" && (
                <Button variant="outline" size="sm" onClick={() => reopen(it.id)} disabled={busyIds.has(it.id)}>
                  <RotateCcw className="w-3.5 h-3.5" />
                </Button>
              )}
              {isAdmin && (
                <Button variant="ghost" size="sm" onClick={() => remove(it.id)} disabled={busyIds.has(it.id)} data-testid={`kasa-delete-${it.id}`}>
                  <Trash2 className="w-3.5 h-3.5 text-red-400" />
                </Button>
              )}
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!previewUrl} onOpenChange={(o) => { if (!o) closePreview(); }}>
        <DialogContent className="max-w-3xl p-2">
          <div className="flex items-center justify-between px-2 py-1 border-b border-slate-100 mb-2">
            <div className="text-sm font-medium flex items-center gap-1"><Receipt className="w-4 h-4" /> Fiş Önizleme</div>
            <button onClick={closePreview} className="text-slate-500 hover:text-slate-800"><X className="w-4 h-4" /></button>
          </div>
          {previewUrl && <img src={previewUrl} alt="Fiş" className="w-full max-h-[70vh] object-contain rounded" data-testid="kasa-receipt-preview" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TabBtn({ active, onClick, children, tid, highlight }) {
  return (
    <button data-testid={tid} onClick={onClick}
      className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-all ${
        active ? (highlight ? "border-emerald-500 text-emerald-300" : "border-[color:var(--y-accent)] text-[color:var(--y-accent)]")
              : "border-transparent text-[color:var(--y-muted)] hover:text-[color:var(--y-text)]"
      }`}>{children}</button>
  );
}

function NewExpenseForm({ onDone }) {
  const [desc, setDesc] = useState("");
  const [amount, setAmount] = useState("");
  const [cat, setCat] = useState("diger");
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const onFile = (f) => {
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) { toast.error("Dosya çok büyük (max 15MB)"); return; }
    setPhoto(f);
    setPhotoPreview(URL.createObjectURL(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    const a = parseFloat((amount || "").replace(",", "."));
    if (!desc.trim()) { toast.error("Açıklama boş olamaz"); return; }
    if (!(a > 0)) { toast.error("Tutar 0'dan büyük olmalı"); return; }
    const fd = new FormData();
    fd.append("description", desc.trim());
    fd.append("amount_tl", a);
    fd.append("category", cat);
    if (photo) fd.append("receipt", photo);
    setSubmitting(true);
    try {
      await api.post("/expenses", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Masraf kaydedildi");
      setDesc(""); setAmount(""); setCat("diger"); setPhoto(null); setPhotoPreview(null);
      onDone && onDone();
    } catch (er) {
      toast.error(formatApiErrorDetail(er.response?.data?.detail) || "Kaydedilemedi");
    } finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="kasa-new-form">
      <div>
        <label className="text-[11px] uppercase tracking-widest text-[color:var(--y-muted)]">Açıklama</label>
        <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={2} placeholder="Örn: Sanayi'den 2 adet aküster kablosu" data-testid="kasa-new-desc" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[11px] uppercase tracking-widest text-[color:var(--y-muted)]">Tutar (₺)</label>
          <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0,00" data-testid="kasa-new-amount" />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest text-[color:var(--y-muted)]">Kategori</label>
          <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="kasa-new-cat"
            className="h-10 w-full bg-[color:var(--y-surface)] border border-[color:var(--y-border)] rounded-md px-3 text-sm text-[color:var(--y-text)]">
            {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="text-[11px] uppercase tracking-widest text-[color:var(--y-muted)] flex items-center gap-2">
          <Camera className="w-3.5 h-3.5" /> Fiş Fotoğrafı (opsiyonel)
        </label>
        <input type="file" accept="image/*" capture="environment" onChange={(e) => onFile(e.target.files?.[0])} data-testid="kasa-new-photo"
          className="text-sm mt-1 text-[color:var(--y-text-2)]" />
        {photoPreview && <img src={photoPreview} alt="önizleme" className="mt-2 max-h-40 rounded border border-[color:var(--y-border)]" />}
      </div>
      <Button type="submit" disabled={submitting} className="w-full" data-testid="kasa-new-submit">
        {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
        Masraf Kaydet
      </Button>
    </form>
  );
}
